// --- SpellcheckEditor.jsx ---
import React, { useMemo, useCallback, useState, useEffect } from "react";
import { createEditor, Text, Transforms, Editor } from "slate";
import { Slate, Editable, withReact } from "slate-react";
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { stringToSlate, slateToString } from './TagTextField';
import { resolveTagName } from '../utils/tagDisplay';
import './TagTextField.css';

const EMPTY = [{ type: 'paragraph', children: [{ text: '' }] }];
const normalizeSlateValue = (v) => (Array.isArray(v) && v.length ? v : EMPTY);

export default function SpellcheckEditor({
    value = '',
    onChange,
    errors = [],
    readOnly = false,
    onReplace = () => { },
    tagMap = {},
    ...props
}) {
    const editor = useMemo(() => {
        const e = withReact(createEditor());
        const { isInline, isVoid } = e;
        e.isInline = (element) => (element.type === 'tag' ? true : isInline(element));
        e.isVoid = (element) => (element.type === 'tag' ? true : isVoid(element));
        return e;
    }, []);

    // Parse & normalize incoming value
    const [val, setVal] = useState(() =>
        normalizeSlateValue(Array.isArray(value) ? value : stringToSlate(value))
    );

    // Keep local state in sync when parent value changes
    useEffect(() => {
        const slateVal = normalizeSlateValue(Array.isArray(value) ? value : stringToSlate(value));
        setVal(slateVal);
    }, [value]);

    // Remount Slate when the doc shape changes (prevents stale internal state)
    const valKey = useMemo(() => {
        try { return JSON.stringify(val); } catch { return 'doc'; }
    }, [val]);

    // Convert LanguageTool offsets that include tag markup to Slate offsets without it
    const convertOffset = useCallback((offset) => {
        const text = slateToString(val);
        const regex = /<\/?\d+>/g;
        let diff = 0;
        let m;
        while ((m = regex.exec(text)) && m.index < offset) {
            diff += m[0].length;
        }
        return offset - diff;
    }, [val]);

    // Highlight ranges for errors
    const decorate = useCallback(
        ([node, path]) => {
            const ranges = [];
            if (!Text.isText(node)) return ranges;

            const leafStart = Editor.string(editor, {
                anchor: Editor.start(editor, [0]),
                focus: Editor.start(editor, path),
            }).length;
            const leafEnd = leafStart + node.text.length;

            errors.forEach(err => {
                const start = convertOffset(err.offset);
                const end = convertOffset(err.offset + err.length);
                if (end > leafStart && start < leafEnd) {
                    ranges.push({
                        anchor: { path, offset: Math.max(start, leafStart) - leafStart },
                        focus: { path, offset: Math.min(end, leafEnd) - leafStart },
                        error: err,
                    });
                }
            });
            return ranges;
        },
        [errors, convertOffset, editor]
    );

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedError, setSelectedError] = useState(null);

    const handleLeafClick = (event, error, leaf, attributes) => {
        setSelectedError({ error, leaf, attributes });
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
        setSelectedError(null);
    };

    const renderElement = useCallback(({ attributes, children, element }) => {
        if (element.type === 'tag') {
            const { tag, open } = element;
            const tagName = resolveTagName(tagMap, tag);
            const type = open ? 'open' : 'close';
            const typeLabel = open ? 'opening' : 'closing';
            const capitalizedTypeLabel = typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1);
            const ariaLabel = tagName ? `${tagName} ${typeLabel} tag ${tag}` : `${typeLabel} tag ${tag}`;
            const titleText = tagName ? `${tagName} (${typeLabel} ${tag})` : `${capitalizedTypeLabel} tag ${tag}`;

            return (
                <span
                    {...attributes}
                    data-tag={tag}
                    data-type={type}
                    data-tag-name={tagName || ''}
                    title={titleText}
                    aria-label={ariaLabel}
                    contentEditable={false}
                    draggable
                    onDragStart={e => {
                        e.dataTransfer.setData('text/tag', tag);
                        e.dataTransfer.setData('text/type', type);
                    }}
                    className="inline-tag"
                >
                    <span className="tag-pill" aria-hidden="true">{tag}</span>
                </span>
            );
        }
        return <p {...attributes}>{children}</p>;
    }, [tagMap]);

    const renderLeaf = useCallback(({ attributes, children, leaf }) => {
        if (leaf.error) {
            const { rule } = leaf.error;
            const color =
                rule.issueType === 'misspelling' ? 'red' :
                    rule.issueType === 'grammar' ? 'darkgoldenrod' :
                        rule.category?.id === 'STYLE' ? 'purple' :
                            '#ff9800';

            return (
                <span
                    {...attributes}
                    onClick={e => handleLeafClick(e, leaf.error, leaf, attributes)}
                    style={{
                        textDecorationLine: 'underline',
                        textDecorationStyle: 'wavy',
                        textDecorationColor: color,
                        cursor: 'pointer',
                    }}
                >
                    {children}
                </span>
            );
        }
        return <span {...attributes}>{children}</span>;
    }, [errors]);

    const handleReplace = (suggestion) => {
        if (!selectedError) return;
        const { offset, length, message } = selectedError.error;
        const errorIndex = errors.findIndex(e => e.offset === offset && e.length === length && e.message === message);

        // Replace text in the first paragraph; adjust if you support multi-paragraph edits
        const path = [0, 0];
        const start = convertOffset(offset);
        const end = convertOffset(offset + length);
        Transforms.select(editor, { anchor: { path, offset: start }, focus: { path, offset: end } });
        Transforms.delete(editor);
        Transforms.insertText(editor, suggestion);
        Transforms.deselect(editor);

        onReplace(suggestion, errorIndex);
        handleClose();
    };

    return (
        <>
            <Slate
                key={valKey}
                editor={editor}
                initialValue={val}
                onChange={v => {
                    const next = normalizeSlateValue(v);
                    setVal(next);
                    if (onChange) {
                        if (typeof value === 'string') onChange(slateToString(next));
                        else onChange(next);
                    }
                }}
            >
                <Editable
                    decorate={decorate}
                    renderLeaf={renderLeaf}
                    renderElement={renderElement}
                    readOnly={readOnly}
                    spellCheck={false}
                    {...props}
                />
            </Slate>

            <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <div style={{ padding: 16, minWidth: 180 }}>
                    {selectedError && (() => {
                        const list = selectedError.error.replacements || [];
                        return (
                            <>
                                <Typography variant="subtitle2" color="secondary" gutterBottom>
                                    {selectedError.error.message}
                                </Typography>
                                {list.length > 0 ? (
                                    list.map((r, i) => (
                                        <Button
                                            key={i}
                                            size="small"
                                            variant="outlined"
                                            style={{ marginRight: 8, marginBottom: 8 }}
                                            onClick={() => handleReplace(r.value)}
                                        >
                                            {r.value}
                                        </Button>
                                    ))
                                ) : (
                                    <Typography variant="body2" color="textSecondary">
                                        No suggestions
                                    </Typography>
                                )}
                            </>
                        );
                    })()}
                </div>
            </Popover>
        </>
    );
}
